import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import WarningIcon from '@mui/icons-material/Warning';

import Select from '@shared/atoms/Select';
import FormInput from '@shared/atoms/FormFields/FormInput';
import FullWidthTabs from '@shared/atoms/FullWidthTabs';
import LanguageSelect from '@shared/molecules/LanguageSelect';
import T from '@shared/atoms/Typography';

import {
  contentType,
  DOMAIN_LOCALES,
  LOCALES,
  PROCESSING_STATUS,
} from '@shared/const';
import { langSelectIcons, newSelectTranslation } from '@shared/utils';
import theme from '@shared/themes/default';
import Icon from '@shared/atoms/Icon';
import { useCourse } from '../../../../../contexts/CourseContext';
import { useAppGlobals } from '../../../../../contexts/AppContext';

import AttachmentDrawer from '../../Attachments/components/AttachmentDrawer';
import UploadWithList from '../../Shared/UploadWithList';
import {
  isSupportedLocale,
  parseLanguageFromFilename,
  validateSubtitles,
} from '../../../parseCourseFiles';

import GameInput from './GameInput';
import { DRAWER_ACTIONS } from '../const';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: ${theme.spacing.s};
  padding: ${theme.spacing.m1};
  row-gap: ${theme.spacing.m2};
  width: 506px;
`;

const TitleWrapper = styled.div`
  display: flex;
  column-gap: ${theme.spacing.s};
`;

const LanguageWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
`;

const TabWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.s};
`;

const ErrorWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.s};
`;

const SelectWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing.m3};
`;

const TAB_NAMES = {
  CONTENT: 'content',
  SUBTITLES: 'subtitles',
  ATTACHMENTS: 'attachments',
};

const LESSON_TYPES = {
  NOT_CHOSEN: 'NOT_CHOSEN',
  ...contentType,
};

const DrawerLesson = ({
  course,
  errors,
  item: propsItem,
  locked,
  onChange,
  onSubtitleError,
  setChangedDrawerContent,
  submitting,
  type,
}) => {
  const { t } = useTranslation();
  const { hasChanged, setHasChanged, setUploadingFiles } = useCourse();
  const { domain, getRestClient } = useAppGlobals();

  const languages = DOMAIN_LOCALES[domain];

  const isTrailer =
    type === DRAWER_ACTIONS.ADD_TRAILER || type === DRAWER_ACTIONS.EDIT_TRAILER;

  const [lessonType, setLessonType] = useState(
    !isTrailer ? LESSON_TYPES.NOT_CHOSEN : LESSON_TYPES.VIDEO
  );
  const [item, setItem] = useState(propsItem || {});
  const [language, setLanguage] = useState(languages[0]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [subtitleError, setSubtitleError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const handleOnChange = (force, forcedType) => {
    const type = forcedType || lessonType;
    if (JSON.stringify(propsItem || {}) !== JSON.stringify(item) || force) {
      onChange(
        item,
        (type === LESSON_TYPES.GAME && item.content?.externalGameId) ||
          type === LESSON_TYPES.VIDEO
      );
    }
  };

  useEffect(() => {
    if (type === DRAWER_ACTIONS.EDIT_LESSON) {
      if (propsItem.contentType === contentType.GAME) {
        setLessonType(LESSON_TYPES.GAME);
      } else if (propsItem.name) {
        // If it has name, it's been saved before so it's a video even if no video files have been added
        setLessonType(LESSON_TYPES.VIDEO);
      }
    }
  }, [propsItem]);

  useEffect(() => {
    handleOnChange();
  }, [item]);

  useEffect(() => {
    setSelectedTab(0);
    if (lessonType === LESSON_TYPES.VIDEO) {
      setItem(item);
    } else {
      setChangedDrawerContent(false);
    }
  }, [lessonType]);

  useEffect(() => {
    if (onSubtitleError) {
      onSubtitleError(subtitleError);
    }
  }, [subtitleError, onSubtitleError]);

  const handleUpload = (tab, files) => {
    if (files?.length > 0) {
      setItem((prevItem) => {
        let newItem;
        switch (tab) {
          case TAB_NAMES.CONTENT:
            newItem = {
              ...prevItem,
              content: {
                ...prevItem.content,
                duration: 0,
                file: {
                  processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                  file: files[0].s3File,
                },
                videoFiles: [],
              },
            };
            break;
          case TAB_NAMES.SUBTITLES: {
            const existingSubtitles = prevItem?.content?.subtitles || [];

            const parsedFiles = files.map((file) => {
              const langCode = parseLanguageFromFilename(file.name);
              return { file, langCode };
            });

            const newSubtitles = [...existingSubtitles];
            parsedFiles.forEach(({ file, langCode }) => {
              newSubtitles.push({
                file: {
                  file: file.s3File,
                  processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                },
                fileName: file.name,
                language: { code: langCode },
                hasError: !langCode,
              });
            });

            const { hasUnknownLang, hasDuplicates } =
              validateSubtitles(newSubtitles);

            if (hasUnknownLang) {
              setSubtitleError(
                t('backoffice.courses.lessonsTab.missingLanguageCode')
              );
            } else if (hasDuplicates) {
              setSubtitleError(
                t('backoffice.courses.lessonsTab.duplicateLanguageError')
              );
            } else {
              setSubtitleError(null);
            }

            newItem = {
              ...prevItem,
              content: {
                ...(prevItem?.content || {}),
                subtitles: newSubtitles,
              },
            };
            break;
          }
          case TAB_NAMES.ATTACHMENTS:
            newItem = {
              ...prevItem,
              content: {
                ...(prevItem?.content || {}),
                attachments: [
                  ...(prevItem?.content?.attachments || []),
                  ...files.map((file) => ({
                    file: {
                      file: files[0].s3File,
                      processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                    },
                    name: languages.map((lang) => ({
                      languageCode: lang,
                      value: file.name,
                    })),
                  })),
                ],
              },
              attachments: [
                ...(prevItem?.attachments || []),
                ...files.map((file) => ({
                  name: languages.map((lang) => ({
                    languageCode: lang,
                    value: file.name,
                  })),
                  file: {
                    file: file.s3File,
                    processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                  },
                })),
              ],
            };
            break;
          default:
            break;
        }
        return newItem;
      });
    }
    setUploadingFiles(false);
  };

  const handleDelete = (tab, index) => {
    setItem((prevItem) => {
      let newItem;
      switch (tab) {
        case TAB_NAMES.CONTENT:
          newItem = {
            ...prevItem,
            content: {
              ...prevItem.content,
              duration: 0,
              file: {
                processingStatus: null,
              },
              videoFiles: [],
            },
          };
          break;
        case TAB_NAMES.SUBTITLES: {
          const remainingSubtitles = prevItem.content.subtitles.filter(
            (_, idx) => index !== idx
          );
          const { hasUnknownLang, hasDuplicates } =
            validateSubtitles(remainingSubtitles);

          if (hasUnknownLang) {
            setSubtitleError(
              t('backoffice.courses.lessonsTab.missingLanguageCode')
            );
          } else if (hasDuplicates) {
            setSubtitleError(
              t('backoffice.courses.lessonsTab.duplicateLanguageError')
            );
          } else {
            setSubtitleError(null);
          }
          newItem = {
            ...prevItem,
            content: {
              ...prevItem.content,
              subtitles: remainingSubtitles,
            },
          };
          break;
        }
        case TAB_NAMES.ATTACHMENTS:
          newItem = {
            ...prevItem,
            attachments: prevItem.attachments.filter((_, idx) => index !== idx),
          };
          break;
        default:
          break;
      }
      return newItem;
    });
  };

  const handleChange = (field, value) => {
    setItem((prevItem) => {
      const newItem = { ...prevItem, [field]: value };
      if (!newItem[field]) {
        delete newItem[field];
      }

      return newItem;
    });
  };

  const handleAttachmentUpdate = ({ name, ext }) => {
    const newAttachments = [...item.attachments];
    newAttachments[selectedIndex] = {
      ...newAttachments[selectedIndex],
      name: name.map((translation) => ({
        languageCode: translation.languageCode,
        value: [translation.value, ext].join('.'),
      })),
    };
    handleChange('attachments', newAttachments);
    setOpen(false);
    setHasChanged(true);
    setChangedDrawerContent(true);
  };

  const onCloseAttachmentDrawer = () => {
    setOpen(false);
    setSelectedIndex(-1);
  };

  const handleModify = (index) => {
    setSelectedIndex(index);
    setOpen(true);
  };

  const handleTitleChange = ({ value }) => {
    const names = [...(item?.name ?? [])];

    const titleIndex =
      names.findIndex((name) => name.languageCode === language) ?? 0;

    const [currentTitle, restTitles] = names.reduce(
      ([current, rest], name) => {
        if (name.languageCode === language) {
          return [{ ...name }, rest];
        }
        rest.push(name);
        return [current, rest];
      },
      [{}, []]
    );

    if (value) {
      currentTitle.languageCode = language;
      currentTitle.value = value;
      restTitles.splice(titleIndex, 0, currentTitle);
    }

    handleChange('name', restTitles);
  };

  const onStartUpload = () => {
    if (!hasChanged) setHasChanged(true);
    setChangedDrawerContent(true);
    setUploadingFiles(true);
  };

  const handleSubtitleDownload = async (episodeId, languageCode) => {
    try {
      const restClient = getRestClient();
      const { data } = await restClient.get(
        `/episodes/${episodeId}/subtitles/${languageCode}/download`
      );
      const a = document.createElement('a');
      a.href = data.url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      setSubtitleError(
        t('backoffice.courses.lessonsTab.subtitleDownloadError')
      );
    }
  };

  const getTabItems = (tab) => {
    let value;
    switch (tab) {
      case TAB_NAMES.CONTENT:
        if (isTrailer) {
          value =
            item.content?.videoFiles?.length > 0 ||
            item.content?.file?.processingStatus
              ? [
                  {
                    id: course.id || 'trailer-1',
                    name: t('backoffice.courses.lessonsTab.videoFile'),
                    processingStatus: item.content.file?.processingStatus,
                  },
                ]
              : [];
        } else if (lessonType === LESSON_TYPES.VIDEO) {
          value =
            item.content?.videoFiles?.length > 0 ||
            item.content?.file?.processingStatus
              ? [
                  {
                    id: item.id || 'video-1',
                    name: t('backoffice.courses.lessonsTab.videoFile'),
                    processingStatus:
                      item.content.file?.processingStatus ||
                      PROCESSING_STATUS.PROCESSED,
                  },
                ]
              : [];
        } else if (lessonType === LESSON_TYPES.GAME) {
          value = [];
        } else {
          value = [];
        }
        break;
      case TAB_NAMES.SUBTITLES:
        value = (item.content?.subtitles || []).map((subtitle, index) => ({
          id: `subtitle-${subtitle.language.code}-${index}`,
          name: subtitle.fileName
            ? subtitle.fileName
            : t('backoffice.courses.lessonsTab.subtitles'),
          processingStatus: subtitle.file?.processingStatus,
          secondaryIcon: isSupportedLocale(subtitle.language.code) ? (
            <Icon
              svgContent={langSelectIcons[subtitle.language.code]}
              viewBox="0 0 32 16"
            />
          ) : (
            <Icon
              icon={WarningIcon}
              color={theme.palette.errorDark}
              size="1rem"
            />
          ),
          actions: [
            {
              ariaLabel: t('generic.download'),
              icon: DownloadIcon,
              handler: () =>
                handleSubtitleDownload(item.id, subtitle.language.code),
              disabled:
                subtitle.file?.processingStatus !== PROCESSING_STATUS.PROCESSED,
              showWhenDisabled: true,
            },
            {
              ariaLabel: t('generic.delete'),
              icon: DeleteOutlineIcon,
              color: theme.palette.pureDark,
              handler: (idx) =>
                handleDelete(TAB_NAMES.SUBTITLES, Number.parseInt(idx, 10)),
            },
          ],
        }));
        break;
      case TAB_NAMES.ATTACHMENTS:
        value = item.attachments
          ? item.attachments.map(({ name, file }, index) => ({
              id: `attachment-${index}`,
              name: newSelectTranslation(name, language),
              processingStatus: file.processingStatus,
            }))
          : [];
        break;
      default:
        value = [];
        break;
    }
    return value;
  };

  let tabs = [TAB_NAMES.CONTENT, TAB_NAMES.SUBTITLES, TAB_NAMES.ATTACHMENTS];
  if (isTrailer) {
    tabs = [TAB_NAMES.CONTENT, TAB_NAMES.SUBTITLES];
  }
  if (lessonType === LESSON_TYPES.GAME) {
    tabs = [TAB_NAMES.CONTENT, TAB_NAMES.ATTACHMENTS];
  }

  const getTabs = () => {
    return tabs.map((tab) => {
      const listItemsActions = [
        {
          ariaLabel: t('generic.delete'),
          icon: DeleteOutlineIcon,
          color: theme.palette.pureDark,
          handler: (idx) => handleDelete(tab, Number.parseInt(idx, 10)),
        },
      ];
      const tabItems = getTabItems(tab);
      let maxFiles;
      let description;
      if (tab === TAB_NAMES.CONTENT) {
        maxFiles = 1;
        if (isTrailer) {
          description = [t('backoffice.courses.lessonsTab.trailerUpload')];
        } else {
          description = [t('backoffice.courses.lessonsTab.contentUpload')];
        }
      } else if (tab === TAB_NAMES.SUBTITLES) {
        maxFiles = 2;
        description = [t('backoffice.courses.lessonsTab.subtitlesUploadMulti')];
      } else if (tab === TAB_NAMES.ATTACHMENTS) {
        description = [t('backoffice.courses.lessonsTab.attachmentsUpload')];
        listItemsActions[0].label = t(
          'backoffice.courses.attachmentsTab.deleteFilename'
        );
        listItemsActions.push({
          label: t('backoffice.courses.attachmentsTab.modifyFilename'),
          handler: (idx) => handleModify(Number.parseInt(idx, 10)),
        });
      }

      // this tab won't be using the Upload component
      if (tab === TAB_NAMES.CONTENT && lessonType === LESSON_TYPES.GAME) {
        return {
          label: t('backoffice.courses.lessonsTab.content'),
          component: (
            <GameInput
              onSubmit={(game) => {
                setItem((prevItem) => ({
                  ...prevItem,
                  content: {
                    ...(prevItem?.content || {}),
                    ...game,
                  },
                }));
              }}
              currentGame={propsItem?.content || {}}
            />
          ),
        };
      }

      return {
        label: `${t(`backoffice.courses.lessonsTab.${tab}`)}${
          tabItems.length > 0 ? ` (${tabItems.length})` : ''
        }`,
        component: (
          <>
            {tab === TAB_NAMES.SUBTITLES && subtitleError && (
              <T
                color={theme.palette.errorDark}
                fontWeight="semibold"
                variant="paragraphS"
                style={{ marginBottom: theme.spacing.s }}
              >
                {subtitleError}
              </T>
            )}
            {tab === TAB_NAMES.ATTACHMENTS && item.attachments && (
              <AttachmentDrawer
                attachment={item.attachments[selectedIndex]}
                onClose={onCloseAttachmentDrawer}
                onSave={handleAttachmentUpdate}
                open={open}
              />
            )}
            <UploadWithList
              actions={listItemsActions}
              description={
                locked
                  ? [t('backoffice.courses.lessonsTab.unlockToUpload')]
                  : description
              }
              disabled={locked || submitting}
              height="100%"
              items={tabItems}
              key={tabItems.length}
              maxFiles={maxFiles}
              onStartUpload={() => {
                if (tab === TAB_NAMES.SUBTITLES) {
                  setSubtitleError(null);
                }
                onStartUpload();
              }}
              onUpload={(files) => handleUpload(tab, files)}
              uploadHeight="100%"
            />
          </>
        ),
      };
    });
  };

  const getLessonTypes = () => [
    {
      value: LESSON_TYPES.NOT_CHOSEN,
      key: LESSON_TYPES.NOT_CHOSEN,
      label: t('backoffice.courses.lessonsTab.notChosen'),
    },
    {
      value: LESSON_TYPES.VIDEO,
      key: LESSON_TYPES.VIDEO,
      label: t('backoffice.courses.lessonsTab.video'),
    },
    {
      value: LESSON_TYPES.GAME,
      key: LESSON_TYPES.GAME,
      label: t('backoffice.courses.lessonsTab.game'),
    },
  ];

  return (
    <SectionStyled>
      {!isTrailer && (
        <HeaderWrapper>
          <TitleWrapper>
            {languages.length > 1 && (
              <LanguageWrapper>
                <LanguageSelect
                  languages={languages}
                  onChange={({ value }) => setLanguage(value)}
                  required
                  value={language}
                />
              </LanguageWrapper>
            )}
            <FormInput
              disabled={locked || submitting}
              id="title"
              key="title"
              label={t('backoffice.courses.lessonsTab.title')}
              name="title"
              handleChange={handleTitleChange}
              placeholder={t('backoffice.courses.lessonsTab.titlePlaceholder')}
              properties={{
                title: {
                  maxLength: 70,
                },
              }}
              required
              showMaxLength
              theme="dark"
              value={newSelectTranslation(item.name, language, '')}
            />
          </TitleWrapper>
          {errors.length > 0 && (
            <ErrorWrapper>
              {errors.map((lang) => (
                <T
                  color={theme.palette.errorDark}
                  fontWeight="semibold"
                  key={lang}
                  variant="paragraphS"
                >
                  {t(`backoffice.courses.lessonsTab.${lang}Missing`)}
                </T>
              ))}
            </ErrorWrapper>
          )}
        </HeaderWrapper>
      )}
      <TabWrapper>
        {type !== DRAWER_ACTIONS.ADD_TRAILER &&
        type !== DRAWER_ACTIONS.EDIT_TRAILER ? (
          <>
            {(type === DRAWER_ACTIONS.ADD_LESSON ||
              lessonType === LESSON_TYPES.NOT_CHOSEN) && (
              <SelectWrapper>
                <Select
                  fullWidth={false}
                  id="contentTypeSelect"
                  items={getLessonTypes()}
                  name="contentTypeSelect"
                  onChange={({ value }) => {
                    setItem({ ...item, contentType: value });
                    setLessonType(value);
                    handleOnChange(true, value);
                  }}
                  value={lessonType}
                />
              </SelectWrapper>
            )}
            {lessonType !== LESSON_TYPES.NOT_CHOSEN && (
              <FullWidthTabs
                centerTabs
                contentHeight="100%"
                onChangeTabHandler={(index) => setSelectedTab(index)}
                selectedTabIndex={selectedTab}
                tabsToSelect={getTabs()}
                theme="dark"
                wrapperStyle={`
                  display: flex;
                  flex-direction: column;
                `}
              />
            )}
          </>
        ) : (
          <FullWidthTabs
            centerTabs
            contentHeight="100%"
            onChangeTabHandler={(index) => setSelectedTab(index)}
            selectedTabIndex={selectedTab}
            tabsToSelect={getTabs()}
            theme="dark"
            wrapperStyle={`
              display: flex;
              flex-direction: column;
            `}
          />
        )}
      </TabWrapper>
    </SectionStyled>
  );
};

DrawerLesson.propTypes = {
  course: PropTypes.object.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string),
  item: PropTypes.shape({
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.arrayOf(
          PropTypes.shape({
            languageCode: PropTypes.oneOf(LOCALES),
            value: PropTypes.string,
          })
        ),
      })
    ),
    contentType: PropTypes.string,
    content: PropTypes.object,
    id: PropTypes.string,
    name: PropTypes.arrayOf(
      PropTypes.shape({
        languageCode: PropTypes.oneOf(LOCALES),
        value: PropTypes.string,
      })
    ),
  }),
  locked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onSubtitleError: PropTypes.func,
  setChangedDrawerContent: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  type: PropTypes.oneOf(Object.values(DRAWER_ACTIONS)),
};

DrawerLesson.defaultProps = {
  locked: false,
  submitting: false,
};

export default DrawerLesson;
